import { getJwtFromCookie, getSessionToken, isSSR } from "@repo/constants";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export enum CookieName {
  IBX = "ibx",
  SESSION_ID = "ib_sessionid",
}

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface HttpRequestOptions {
  headers?: HeadersInit;
  signal?: AbortSignal;
  credentials?: RequestCredentials;
  queryParams?: QueryParams;
  onResponse?: (response: Response) => void;
}

export interface HttpWriteOptions extends HttpRequestOptions {
  body?: unknown;
}

function buildUrl(url: string, queryParams?: QueryParams): string {
  if (!queryParams || Object.keys(queryParams).length === 0) {
    return url;
  }

  const searchParams = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  if (!queryString) {
    return url;
  }

  return `${url}${url.includes("?") ? "&" : "?"}${queryString}`;
}

function buildHeaders(headers?: HeadersInit, body?: unknown): Headers {
  const finalHeaders = new Headers(headers);
  if (body !== undefined && !finalHeaders.has("Content-Type")) {
    finalHeaders.set("Content-Type", "application/json");
  }
  return finalHeaders;
}

async function request<T>(
  method: HttpMethod,
  url: string,
  options: HttpWriteOptions = {},
): Promise<T> {
  const { body, headers, signal, credentials, queryParams, onResponse } = options;
  const finalUrl = buildUrl(url, queryParams);

  const response = await fetch(finalUrl, {
    method,
    headers: buildHeaders(headers, body),
    signal,
    credentials: credentials ?? "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  onResponse?.(response);

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as T;
  }

  return (await response.text()) as T;
}

export function get<T>(
  url: string,
  options: HttpRequestOptions = {},
): Promise<T> {
  return request<T>("GET", url, options);
}

export function post<T>(
  url: string,
  body?: unknown,
  options: HttpRequestOptions = {},
): Promise<T> {
  return request<T>("POST", url, { ...options, body });
}

export function put<T>(
  url: string,
  body?: unknown,
  options: HttpRequestOptions = {},
): Promise<T> {
  return request<T>("PUT", url, { ...options, body });
}

export function patch<T>(
  url: string,
  body?: unknown,
  options: HttpRequestOptions = {},
): Promise<T> {
  return request<T>("PATCH", url, { ...options, body });
}

export function del<T>(
  url: string,
  body?: unknown,
  options: HttpRequestOptions = {},
): Promise<T> {
  return request<T>("DELETE", url, { ...options, body });
}

export type ExtraHeaders =
  | Record<string, string>
  | (() => Record<string, string> | Promise<Record<string, string>>);

export interface UseHttpConfig {
  baseUrl?: string;
  onError?: (message: string) => void;
  onResponse?: (response: Response) => void;
  getToken?: () => string | null | Promise<string | null>;
  authHeaderName?: string;
  authScheme?: string;
  extraHeaders?: ExtraHeaders;
}

async function resolveExtraHeaders(extraHeaders?: ExtraHeaders): Promise<Record<string, string>> {
  if (typeof extraHeaders === "function") {
    return await extraHeaders();
  }
  return extraHeaders ?? {};
}

// Variable path prevents bundlers from statically analyzing this import,
// so next/headers is never pulled into the client bundle. Falls back to
// empty headers on the client (where the try will throw).
async function resolveSSRHeaders(
  authHeaderName: string,
  authScheme: string,
  extraHeaders?: ExtraHeaders,
): Promise<Record<string, string>> {
  const resolvedExtraHeaders = await resolveExtraHeaders(extraHeaders);
  try {
    const mod = "next/headers";
    const { cookies } = (await import(mod)) as any;
    const cookieStore = await cookies();
    const ibxToken: string | undefined = cookieStore.get(CookieName.IBX)?.value;
    const sessionId: string | undefined = cookieStore.get(CookieName.SESSION_ID)?.value;
    const cookieParts = [
      ibxToken && `${CookieName.IBX}=${ibxToken}`,
      sessionId && `${CookieName.SESSION_ID}=${sessionId}`,
    ].filter(Boolean).join("; ");
    return {
      "Content-Type": "application/json",
      ...(cookieParts ? { Cookie: cookieParts } : {}),
      ...(ibxToken ? { [authHeaderName]: `${authScheme} ${ibxToken}` } : {}),
      ...resolvedExtraHeaders,
    };
  } catch {
    return { "Content-Type": "application/json", ...resolvedExtraHeaders };
  }
}

export function useHttp(config: UseHttpConfig = {}) {
  const {
    baseUrl = process.env.NEXT_PUBLIC_API_URL,
    getToken,
    onError,
    authHeaderName = "x-ib-authorization",
    authScheme = "Bearer",
    extraHeaders,
  } = config;

  async function authHeaders(): Promise<Record<string, string>> {
    let token = await getToken?.();
    if(!token){
      token = getJwtFromCookie() || getSessionToken();
    }
    const resolvedExtraHeaders = await resolveExtraHeaders(extraHeaders);
    return {
      "Content-Type": "application/json",
      ...(token ? { [authHeaderName]: `${authScheme} ${token}` } : {}),
      ...resolvedExtraHeaders,
    };
  }

  async function run<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      onError?.((e as Error).message);
      throw e;
    }
  }

  async function opts() {
    if (isSSR()) {
      const headers = await resolveSSRHeaders(authHeaderName, authScheme, extraHeaders);
      return { headers, credentials: "omit" as RequestCredentials, onResponse: config.onResponse };
    }
    return { headers: await authHeaders(), onResponse: config.onResponse };
  }

  return {
    get: <T>(path: string) => run(async () => get<T>(`${baseUrl}${path}`, await opts())),
    post: <T>(path: string, body: unknown) => run(async () => post<T>(`${baseUrl}${path}`, body, await opts())),
    put: <T>(path: string, body: unknown) => run(async () => put<T>(`${baseUrl}${path}`, body, await opts())),
    patch: <T>(path: string, body: unknown) => run(async () => patch<T>(`${baseUrl}${path}`, body, await opts())),
    del: <T>(path: string, body?: unknown) => run(async () => del<T>(`${baseUrl}${path}`, body, await opts())),
  };
}